import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import type { Court, CourtHireBooking, CourtHireRate, PaginatedResponse } from '@court-sync/shared';
import {
  TierFeatureKey,
  formatCourtHireBookerName,
  resolveCourtHireBookerKind,
} from '@court-sync/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { ClubFeatureGate } from '@/components/club/ClubFeatureGate';
import { ClubCourtHireSettingsTab } from '@/components/club/ClubCourtHireSettingsTab';
import { CourtHireRateDrawer } from '@/components/club/CourtHireRateDrawer';
import {
  Badge,
  Button,
  Input,
  PaginatedTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { courtHireService } from '@/services/courtHire';
import { clubService } from '@/services/club';
import { formatCurrency, getCurrencyFromRegion, type Currency } from '@/utils/currency';
import { Clock, MoreVertical, Pencil, Plus, Search, Tag, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { useTour } from '@/components/ui/Tour';
import { CLUB_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  CLUB_COURT_HIRE_PAGE_HELP,
  CLUB_COURT_HIRE_TOUR_KEY,
  getClubCourtHireTabHelp,
  scheduleClubCourtHireTourStart,
} from '@/lib/tours/clubCourtHireTour';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';

type CourtHireTab = 'bookings' | 'rates' | 'settings';

const COURT_HIRE_TABS: {
  id: CourtHireTab;
  label: string;
  mobileLabel: string;
  tabId: string;
}[] = [
  {
    id: 'bookings',
    label: 'Bookings',
    mobileLabel: 'Bookings',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_BOOKINGS,
  },
  {
    id: 'rates',
    label: 'Rates',
    mobileLabel: 'Rates',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_RATES,
  },
  {
    id: 'settings',
    label: 'Settings',
    mobileLabel: 'Settings',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_SETTINGS,
  },
];

function parseCourtHireTab(value: string | null): CourtHireTab {
  if (value === 'rates') return 'rates';
  if (value === 'settings') return 'settings';
  return 'bookings';
}

const BOOKINGS_PAGE_SIZE = 20;

const BOOKING_STATUS_LABELS: Record<CourtHireBooking['status'], string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

const BOOKING_STATUS_BADGE_VARIANT: Record<
  CourtHireBooking['status'],
  'success' | 'warning' | 'secondary' | 'destructive'
> = {
  pending: 'warning',
  confirmed: 'success',
  cancelled: 'destructive',
  completed: 'secondary',
};

function isPaginatedBookingsResponse(
  res: { success: boolean; data?: CourtHireBooking[]; pagination?: PaginationState }
): res is PaginatedResponse<CourtHireBooking> {
  return res.success && res.pagination != null;
}

function formatBookingWhen(booking: CourtHireBooking): string {
  const start = booking.startAt instanceof Date ? booking.startAt : new Date(booking.startAt);
  const end = booking.endAt instanceof Date ? booking.endAt : new Date(booking.endAt);
  return `${format(start, 'd MMM yyyy HH:mm')} – ${format(end, 'HH:mm')}`;
}

function CourtHireBookerCell({ booking }: { booking: CourtHireBooking }) {
  const displayName = formatCourtHireBookerName(booking);
  const bookerKind = resolveCourtHireBookerKind(booking);
  const isMember = bookerKind === 'member' && booking.memberId;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {isMember ? (
          <Link
            to={`/admin/members?member=${booking.memberId}`}
            className="font-medium text-primary hover:underline"
          >
            {displayName}
          </Link>
        ) : (
          <p className="font-medium text-gray-900 dark:text-white">{displayName}</p>
        )}
        <Badge variant={isMember ? 'secondary' : 'outline'}>
          {isMember ? 'Member' : 'Guest'}
        </Badge>
      </div>
      {booking.guestPhone ? (
        <p className="text-sm text-muted-foreground">
          <a href={`tel:${booking.guestPhone}`} className="hover:underline">
            {booking.guestPhone}
          </a>
        </p>
      ) : !isMember && booking.guestEmail ? (
        <p className="text-sm text-muted-foreground">{booking.guestEmail}</p>
      ) : null}
    </div>
  );
}

function formatBookingPrice(booking: CourtHireBooking): string {
  return formatCurrency(booking.priceCents / 100, booking.currency as Currency);
}

export default function ClubCourtHire() {
  const { currentClub, setCurrentClub } = useCurrentClub();
  const { user } = useAuthStore();
  const { startPageTour, startTourWithCompletionKey } = usePageTourActions();
  const { isActive: isTourActive } = useTour();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseCourtHireTab(searchParams.get('tab'));
  const setActiveTabRef = useRef<(tab: CourtHireTab) => void>(() => {});
  // Court hire currency resolves from the club's market, not the signed-in operator's region.
  const currency = getCurrencyFromRegion(currentClub?.region ?? undefined);

  const setActiveTab = useCallback(
    (tab: CourtHireTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'bookings') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setActiveTabRef.current = setActiveTab;
  }, [setActiveTab]);

  const [bookingRows, setBookingRows] = useState<CourtHireBooking[]>([]);
  const [bookingPagination, setBookingPagination] = useState<PaginationState | null>(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState<CourtHireBooking['status'] | 'all'>('all');
  const [rates, setRates] = useState<CourtHireRate[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [rateDrawerOpen, setRateDrawerOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<CourtHireRate | null>(null);
  const rateCreateTourScheduledRef = useRef(false);
  const cancelRateCreateTourScheduleRef = useRef<(() => void) | null>(null);

  const courtNameById = useMemo(
    () => Object.fromEntries(courts.map((court) => [court.id, court.name] as const)),
    [courts]
  );

  const courtName = useCallback(
    (courtId: string): string => courtNameById[courtId] ?? courtId,
    [courtNameById]
  );

  const loadCourts = useCallback(async () => {
    if (!currentClub) return;
    const courtsRes = await clubService.getClubCourts(currentClub.id);
    setCourts(courtsRes.success && courtsRes.data ? courtsRes.data : []);
  }, [currentClub]);

  const loadRates = useCallback(async () => {
    if (!currentClub) return;
    setRatesLoading(true);
    try {
      const ratesRes = await courtHireService.getRates(currentClub.id);
      setRates(ratesRes.success && ratesRes.data ? ratesRes.data : []);
    } finally {
      setRatesLoading(false);
    }
  }, [currentClub]);

  const loadBookingsPage = useCallback(
    async (page: number) => {
      if (!currentClub) return;
      setBookingsLoading(true);
      try {
        const res = await courtHireService.getBookings(currentClub.id, {
          page,
          limit: BOOKINGS_PAGE_SIZE,
          search: bookingSearchQuery || undefined,
          ...(bookingStatusFilter !== 'all' ? { status: bookingStatusFilter } : {}),
        });
        if (isPaginatedBookingsResponse(res)) {
          setBookingRows(res.data ?? []);
          setBookingPagination(res.pagination);
        } else if (res.success && res.data) {
          setBookingRows(res.data);
          setBookingPagination({
            page: 1,
            limit: res.data.length,
            total: res.data.length,
            totalPages: 1,
          });
        } else {
          setBookingRows([]);
          setBookingPagination(null);
        }
      } finally {
        setBookingsLoading(false);
      }
    },
    [bookingSearchQuery, bookingStatusFilter, currentClub]
  );

  const refreshBookingsTab = useCallback(async () => {
    await Promise.all([loadBookingsPage(bookingPage), loadCourts()]);
  }, [bookingPage, loadBookingsPage, loadCourts]);

  useEffect(() => {
    void loadCourts();
  }, [loadCourts]);

  useEffect(() => {
    if (activeTab === 'rates') {
      void loadRates();
    }
  }, [activeTab, loadRates]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = bookingSearchInput.trim();
      setBookingSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [bookingSearchInput]);

  useEffect(() => {
    setBookingPage(1);
  }, [bookingSearchQuery, bookingStatusFilter]);

  useEffect(() => {
    if (activeTab !== 'bookings') return;
    void loadBookingsPage(bookingPage);
  }, [activeTab, bookingPage, loadBookingsPage]);

  const bookingTotalCount = bookingPagination?.total ?? bookingRows.length;

  const bookingTableColumns = useMemo<PaginatedTableColumn<CourtHireBooking>[]>(
    () => [
      {
        key: 'when',
        header: 'When',
        render: (booking) => (
          <span className="whitespace-nowrap text-gray-900 dark:text-white">
            {formatBookingWhen(booking)}
          </span>
        ),
      },
      {
        key: 'court',
        header: 'Court',
        render: (booking) => courtName(booking.courtId),
      },
      {
        key: 'booker',
        header: 'Hirer',
        render: (booking) => <CourtHireBookerCell booking={booking} />,
      },
      {
        key: 'status',
        header: 'Status',
        render: (booking) => (
          <Badge variant={BOOKING_STATUS_BADGE_VARIANT[booking.status]}>
            {BOOKING_STATUS_LABELS[booking.status]}
          </Badge>
        ),
      },
      {
        key: 'price',
        header: 'Price',
        className: 'text-right',
        render: (booking) => (
          <span className="font-medium tabular-nums">{formatBookingPrice(booking)}</span>
        ),
      },
    ],
    [courtName]
  );

  const openCreateRate = useCallback(() => {
    setEditingRate(null);
    setRateDrawerOpen(true);
  }, []);

  const closeRateDrawer = useCallback(() => {
    setRateDrawerOpen(false);
    setEditingRate(null);
  }, []);

  const launchCourtHireTour = useCallback(() => {
    return scheduleClubCourtHireTourStart(startTourWithCompletionKey, {
      onEnterBookings: () => setActiveTabRef.current('bookings'),
      onEnterRates: () => setActiveTabRef.current('rates'),
      onEnterSettings: () => setActiveTabRef.current('settings'),
      onOpenCreateRate: openCreateRate,
      onCloseCreateRate: closeRateDrawer,
      onFinish: () => {
        closeRateDrawer();
        setActiveTabRef.current('bookings');
      },
    });
  }, [closeRateDrawer, openCreateRate, startTourWithCompletionKey]);

  const isCourtHireTourReady =
    Boolean(currentClub) &&
    !bookingsLoading &&
    (activeTab !== 'rates' || !ratesLoading);

  useAutoPageTour(CLUB_COURT_HIRE_TOUR_KEY, isCourtHireTourReady, {
    onStart: launchCourtHireTour,
    afterAnimationMs: 0,
  });

  usePageTourReady(
    'club-court-hire-rate-create',
    rateDrawerOpen && !editingRate && Boolean(currentClub),
  );

  const handleRateCreateDrawerPanelOpen = useCallback(() => {
    if (editingRate || rateCreateTourScheduledRef.current || isTourActive) return;
    if (!isPageTourComplete(user?.preferences, CLUB_COURT_HIRE_TOUR_KEY)) return;
    if (isPageTourComplete(user?.preferences, 'club-court-hire-rate-create')) return;
    rateCreateTourScheduledRef.current = true;
    cancelRateCreateTourScheduleRef.current?.();
    cancelRateCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'club-court-hire-rate-create',
      user?.preferences,
    );
  }, [editingRate, isTourActive, startPageTour, user?.preferences]);

  useEffect(() => {
    if (!rateDrawerOpen) {
      rateCreateTourScheduledRef.current = false;
      cancelRateCreateTourScheduleRef.current?.();
      cancelRateCreateTourScheduleRef.current = null;
    }
  }, [rateDrawerOpen]);

  const courtHireTabHelp = useMemo(() => getClubCourtHireTabHelp(activeTab), [activeTab]);

  const openEditRate = (rate: CourtHireRate) => {
    setEditingRate(rate);
    setRateDrawerOpen(true);
  };

  const handleDeactivateRate = async (rate: CourtHireRate) => {
    if (!currentClub) return;
    if (!window.confirm(`Deactivate "${rate.name}"? It will no longer appear for public booking.`)) {
      return;
    }

    const res = await courtHireService.deactivateRate(currentClub.id, rate.id);
    if (res.success) {
      toast.success('Rate deactivated');
      await loadRates();
    } else {
      toast.error(res.error || 'Failed to deactivate rate');
    }
  };

  const handleDeleteRate = async (rate: CourtHireRate) => {
    if (!currentClub) return;
    if (
      !window.confirm(
        `Delete "${rate.name}"? This permanently removes the rate and cannot be undone.`
      )
    ) {
      return;
    }

    const res = await courtHireService.deleteRate(currentClub.id, rate.id);
    if (res.success) {
      toast.success('Rate deleted');
      await loadRates();
    } else {
      toast.error(res.error || 'Failed to delete rate');
    }
  };

  const ratesHeaderAction =
    activeTab === 'rates' ? (
      <Button
        type="button"
        id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES_NEW}
        onClick={openCreateRate}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {rates.length === 0 ? 'Create rate' : 'New rate'}
      </Button>
    ) : undefined;

  return (
    <ClubFeatureGate feature={TierFeatureKey.COURT_HIRE}>
      <div className="contents">
        <PageHeader
          title="Court hire"
          subtitle="Manage bookings and pricing"
          breadcrumbs={[{ label: 'Court hire', href: '/admin/court-hire' }]}
          actions={ratesHeaderAction}
          help={
            <PageFeatureHelp
              title={CLUB_COURT_HIRE_PAGE_HELP.title}
              tours={[
                {
                  tourKey: CLUB_COURT_HIRE_TOUR_KEY,
                  label: CLUB_COURT_HIRE_PAGE_HELP.tourLabel,
                  description: CLUB_COURT_HIRE_PAGE_HELP.tourDescription,
                  onStart: launchCourtHireTour,
                },
              ]}
            >
              <p>{CLUB_COURT_HIRE_PAGE_HELP.description}</p>
              <p className="mt-2 text-muted-foreground">{courtHireTabHelp.description}</p>
            </PageFeatureHelp>
          }
          helpAnchorId={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_GUIDE}
        />

        <div id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TABS}>
          <DashboardTabNav<CourtHireTab>
            tabs={COURT_HIRE_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            ariaLabel="Court hire sections"
          />
        </div>

        {ratesLoading && activeTab === 'rates' ? (
          <div
            id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES}
            className="flex min-h-[160px] items-center justify-center"
            aria-busy="true"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : activeTab === 'bookings' ? (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_BOOKINGS}>
          {bookingTotalCount === 0 && !bookingsLoading && !bookingSearchQuery && bookingStatusFilter === 'all' ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={Clock} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No court hire bookings</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Guest and member bookings appear here once court hire is live.
              </p>
            </DashboardEmptyStateCard>
          ) : (
            <DashboardDataCard
              toolbar={
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {bookingTotalCount} booking{bookingTotalCount === 1 ? '' : 's'}
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative w-full sm:max-w-xs">
                      <Search
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                      />
                      <Input
                        type="search"
                        value={bookingSearchInput}
                        onChange={(event) => setBookingSearchInput(event.target.value)}
                        placeholder="Search hirer"
                        className="pl-9"
                        aria-label="Search court hire bookings"
                      />
                    </div>
                    <Select
                      value={bookingStatusFilter}
                      onValueChange={(value) =>
                        setBookingStatusFilter(value as CourtHireBooking['status'] | 'all')
                      }
                    >
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {Object.entries(BOOKING_STATUS_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              }
            >
              <PaginatedTable<CourtHireBooking>
                columns={bookingTableColumns}
                data={bookingRows}
                pagination={bookingPagination}
                loading={bookingsLoading}
                emptyMessage={
                  bookingSearchQuery || bookingStatusFilter !== 'all'
                    ? 'No bookings match your filters.'
                    : 'No bookings found.'
                }
                onPageChange={setBookingPage}
                noWrapperPadding
              />
            </DashboardDataCard>
          )}
          </div>
        ) : activeTab === 'settings' && currentClub ? (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_SETTINGS}>
            <ClubCourtHireSettingsTab
              club={currentClub}
              onSaved={(updated) => setCurrentClub(updated)}
            />
          </div>
        ) : activeTab === 'rates' ? (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES}>
          {rates.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={Tag} />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No hire rates</h3>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              Create rates to price court hire sessions by duration and court.
            </p>
            <Button type="button" onClick={openCreateRate}>
              <Plus className="h-4 w-4" aria-hidden />
              Create rate
            </Button>
          </DashboardEmptyStateCard>
          ) : (
          <DashboardDataCard>
            <Table>
              <TableHeader>
                <TableRow className={dashboardTableHeaderRowClass}>
                  <TableHead className={dashboardTableHeadClass}>Name</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Court</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Duration</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Price</TableHead>
                  <TableHead className={dashboardTableHeadClass}>Active</TableHead>
                  <TableHead className={dashboardTableHeadClass}>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((rate) => (
                  <TableRow key={rate.id} className={dashboardTableDataRowClass}>
                    <TableCell className={dashboardTableCellClass}>{rate.name}</TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      {rate.courtId ? (courtNameById[rate.courtId] ?? rate.courtId) : 'All courts'}
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>{rate.durationMinutes} min</TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span>
                          {(rate.priceCents / 100).toFixed(2)} {rate.currency}
                        </span>
                        {rate.membersOnly ? <Badge variant="secondary">Members only</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      {rate.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className={dashboardTableCellClass}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Actions for ${rate.name}`}
                          >
                            <MoreVertical className="h-4 w-4" aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => openEditRate(rate)}>
                            <Pencil className="h-4 w-4" aria-hidden />
                            Edit
                          </DropdownMenuItem>
                          {rate.isActive ? (
                            <DropdownMenuItem onSelect={() => void handleDeactivateRate(rate)}>
                              Deactivate
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => void handleDeleteRate(rate)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DashboardDataCard>
          )}
          </div>
        ) : null}

        {currentClub ? (
          <CourtHireRateDrawer
            clubId={currentClub.id}
            currency={currency}
            courts={courts}
            rate={editingRate}
            isOpen={rateDrawerOpen}
            onClose={closeRateDrawer}
            onCreatePanelOpen={
              !editingRate && !isTourActive ? handleRateCreateDrawerPanelOpen : undefined
            }
            showCreateTourHelp={!editingRate && !isTourActive}
            showBackdrop={!isTourActive}
            onSaved={async () => {
              await loadRates();
              await refreshBookingsTab();
            }}
          />
        ) : null}
      </div>
    </ClubFeatureGate>
  );
}
